import { Link, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import Icon from './Icon';
import ThemeToggle from './ThemeToggle';
import { SECTIONS, SETTINGS_SECTION, isSectionActive } from '@/lib/sections';
import type { Section } from '@/lib/sections';

// Shared frame for every route. Wide screens get a left rail with the
// hub's sections (lib/sections.ts); under 960px the rail becomes the
// old sticky top bar, whose links are icon buttons. Both end with
// Settings and "Send feedback" (a utility page, not a section).
// `wide` widens the content column for multi-column pages (Today).

function NavItem({ section, variant }: { section: Section; variant: 'rail' | 'bar' }) {
  const { pathname } = useLocation();
  const active = isSectionActive(section, pathname);
  return (
    <Link
      to={section.path}
      aria-label={section.label}
      title={variant === 'bar' ? section.label : undefined /* the bar shows icons only */}
      aria-current={active ? 'page' : undefined}
      className={[
        variant === 'rail' ? 'rail-link' : 'nav-link',
        active && 'is-active',
        // Narrow top bar: the brand already links to Today.
        variant === 'bar' && section.id === 'today' && 'is-home',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon name={section.icon} size={variant === 'rail' ? 19 : 20} className="nav-icon" />
      <span className="nav-label">{section.label}</span>
    </Link>
  );
}

// "Send feedback" (routes/Feedback.tsx) carries the page it was opened
// from, which picks the form's default app. Not shown on the page itself.
function FeedbackLink({ variant }: { variant: 'rail' | 'bar' }) {
  const { pathname } = useLocation();
  if (pathname === '/feedback' || pathname.startsWith('/feedback/')) return null;
  const to = `/feedback?from=${encodeURIComponent(pathname).replace(/%2F/g, '/')}`;
  if (variant === 'bar') {
    return (
      <Link to={to} className="nav-link" aria-label="Send feedback" title="Send feedback">
        <Icon name="message" size={20} className="nav-icon" />
      </Link>
    );
  }
  return (
    <Link to={to} className="rail-feedback">
      <Icon name="message" size={17} className="nav-icon" />
      <span>Send feedback</span>
    </Link>
  );
}

function Brand() {
  return (
    <Link to="/" className="brand" aria-label="WaageWu, Today">
      <span className="brand-mark" aria-hidden="true">
        W
      </span>
      <span>WaageWu</span>
    </Link>
  );
}

export default function AppShell({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="shell">
      <aside className="rail" aria-label="Sections">
        <div className="rail-brand">
          <Brand />
        </div>
        <nav className="rail-nav" aria-label="Main">
          {SECTIONS.map((s) => (
            <NavItem key={s.id} section={s} variant="rail" />
          ))}
        </nav>
        <FeedbackLink variant="rail" />
        <div className="rail-foot">
          <NavItem section={SETTINGS_SECTION} variant="rail" />
          <ThemeToggle />
        </div>
      </aside>
      <div className="shell-body">
        <header className="topbar">
          <div className="topbar-inner">
            <Brand />
            <nav className="nav" aria-label="Main">
              {SECTIONS.map((s) => (
                <NavItem key={s.id} section={s} variant="bar" />
              ))}
              <NavItem section={SETTINGS_SECTION} variant="bar" />
              <FeedbackLink variant="bar" />
            </nav>
            <ThemeToggle />
          </div>
        </header>
        <main className={wide ? 'main is-wide' : 'main'}>{children}</main>
      </div>
    </div>
  );
}
